import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Users, Calendar, DollarSign, AlertTriangle, MapPin, ChevronRight, Mail, CheckCircle, Clock, Edit, X, Phone, FileText, Tag, Activity, Crown, Lock, Copy } from 'lucide-react';
import { useDashboardStats, useCustomers, useEvents, useUpsertEvent, AppEvent } from '@/hooks/useFirebaseQueries';
import { useAuth } from '@/contexts/AuthContext';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useProModal } from '@/contexts/ProModalContext';
import { exportToCSV } from '@/utils/csvExport';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SalesOverviewChart } from '@/components/Dashboard/SalesOverviewChart';
import { format, isThisMonth, isAfter, startOfToday } from 'date-fns';
import { es } from 'date-fns/locale/es';
import { useToast } from '@/hooks/use-toast';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { limits, isPro } = usePlanLimits();
  const { openSubscriptionModal } = useProModal();
  const { data: stats, isLoading: statsLoading, error: statsError } = useDashboardStats();
  const { data: customers = [], isLoading: customersLoading } = useCustomers();
  const { data: events = [], isLoading: eventsLoading } = useEvents();
  const upsertEvent = useUpsertEvent();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AppEvent | null>(null);

  const isLoading = statsLoading || customersLoading || eventsLoading;
  const error = statsError;

  const handleExportCSV = () => {
    if (stats?.events) {
      exportToCSV(stats.events, 'events');
    }
  };

  const handleEventClick = (eventId: string) => {
    const event = events.find(e => e.id === eventId);
    if (event) {
      setSelectedEvent(event);
      setIsViewModalOpen(true);
    }
  };

  const handleOpenEdit = () => {
    if (selectedEvent) {
      setIsViewModalOpen(false);
      navigate(`/events/${selectedEvent.id}/edit`);
    }
  };

  const handleCopyUrl = () => {
    if (user?.uid) {
      const url = `${window.location.origin}/q/${user.uid}`;
      navigator.clipboard.writeText(url);
      setIsCopied(true);
      toast({
        title: t('common.success', 'Éxito'),
        description: 'URL copiada al portapapeles',
      });
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleCloseView = () => {
    setIsViewModalOpen(false);
    setSelectedEvent(null);
  };

  // 1. Upcoming Events (This Month, Future)
  const upcomingEvents = useMemo(() => {
    const today = startOfToday();
    return events
      .filter(e => e.date && isThisMonth(new Date(e.date)) && isAfter(new Date(e.date), today))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 4); // Show up to 4
  }, [events]);

  // 1.5 Pending Prospects
  const prospectEvents = useMemo(() => {
    return events
      .filter(e => e.status === 'prospect')
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [events]);

  const handleConfirmProspect = async (e: React.MouseEvent, event: AppEvent) => {
    e.stopPropagation();
    try {
      await upsertEvent.mutateAsync({
        ...event,
        status: 'confirmed'
      });
      toast({
        title: '¡Cotización confirmada!',
        description: 'El evento ha sido confirmado y añadido a tu calendario.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Hubo un error al confirmar la cotización.',
        variant: 'destructive',
      });
    }
  };

  // 2. Top 5 Clients by Revenue
  const topClients = useMemo(() => {
    const clientStats: Record<string, { totalRevenue: number; eventCount: number; name: string }> = {};
    
    events.forEach(event => {
      if (event.customerId && event.status !== 'cancelled' && event.status !== 'prospect') {
        const amount = Number(event.totalWithTax || event.cost || 0);
        if (!clientStats[event.customerId]) {
          const customer = customers.find(c => c.id === event.customerId);
          clientStats[event.customerId] = { 
            totalRevenue: 0, 
            eventCount: 0,
            name: customer ? customer.name : 'Cliente Desconocido'
          };
        }
        clientStats[event.customerId].totalRevenue += amount;
        clientStats[event.customerId].eventCount += 1;
      }
    });

    return Object.values(clientStats)
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 5);
  }, [events, customers]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    console.error("[Dashboard] Error loading stats:", error);
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t('dashboard.error_loading')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">{t('dashboard.title')}</h1>
        <Button onClick={handleExportCSV} variant="outline" className="shadow-sm">
          <Download className="w-4 h-4 mr-2" />
          {t('dashboard.export_csv')}
        </Button>
      </div>

      {/* MULTI-CURRENCY WARNING */}
      {stats?.hasMultipleCurrencies && (
        <Alert className="bg-amber-500/10 border-amber-500/20 text-yellow-600 dark:text-yellow-500">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t('dashboard.multi_currency_warning')}
          </AlertDescription>
        </Alert>
      )}

      {/* PENDING PROSPECTS SECTION */}
      {prospectEvents.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Cotizaciones (Eventos Por Confirmar)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {prospectEvents.map(event => {
              const customer = customers.find(c => c.id === event.customerId);
              return (
                <Card key={event.id} className="cursor-pointer hover:shadow-md transition-shadow group border-amber-500/20 bg-amber-50/30" onClick={() => handleEventClick(event.id)}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-xs font-bold text-amber-600 uppercase tracking-wider">
                        {event.date ? format(new Date(event.date), "d MMM yyyy", { locale: i18n.language === 'es' ? es : undefined }) : 'Sin Fecha'}
                      </div>
                      <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Nuevo</span>
                    </div>
                    <h3 className="font-bold text-lg mb-1 truncate group-hover:text-amber-700 transition-colors">{event.title || 'Cotización'}</h3>
                    <div className="flex items-center text-muted-foreground text-sm mb-3">
                      <Users className="h-3.5 w-3.5 mr-1" />
                      <span className="truncate">{customer ? customer.name : 'Cliente Registrado'}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-amber-500/10 pt-3">
                      <span className="font-bold text-amber-700">
                        {Number(event.totalWithTax || event.cost || 0).toLocaleString(i18n.language, { style: 'currency', currency: event.currency || 'USD' })}
                      </span>
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={(e) => handleConfirmProspect(e, event)}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Confirmar Evento
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* UPCOMING EVENTS SECTION */}
      <div>
        <h2 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Próximos Eventos del Mes
        </h2>
        {upcomingEvents.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {upcomingEvents.map(event => (
              <Card key={event.id} className="cursor-pointer hover:shadow-md transition-shadow group border-primary/10" onClick={() => handleEventClick(event.id)}>
                <CardContent className="p-4">
                  <div className="text-xs font-bold text-primary mb-1 uppercase tracking-wider">
                    {format(new Date(event.date), "d MMM yyyy", { locale: i18n.language === 'es' ? es : undefined })}
                  </div>
                  <h3 className="font-bold text-lg mb-1 truncate group-hover:text-primary transition-colors">{event.title}</h3>
                  <div className="flex items-center text-muted-foreground text-sm mb-3">
                    <MapPin className="h-3.5 w-3.5 mr-1" />
                    <span className="truncate">{event.venue || 'Sin ubicación'}</span>
                  </div>
                  <div className="flex justify-between items-center border-t pt-3">
                    <span className="text-xs text-muted-foreground">A cobrar:</span>
                    <span className="font-bold">
                      {Number(event.totalWithTax || event.cost || 0).toLocaleString(i18n.language, { style: 'currency', currency: event.currency || 'USD' })}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="bg-card border border-dashed rounded-xl p-6 text-center text-muted-foreground">
            No hay eventos programados para el resto de este mes.
          </div>
        )}
      </div>

      {/* MAIN SECTION: Sales Overview + 3 Core Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        
        {/* Sales Overview Chart */}
        <div className="lg:col-span-2 xl:col-span-3">
          {stats?.salesOverview && <SalesOverviewChart data={stats.salesOverview} events={stats?.events || []} payments={stats?.payments || []} />}
        </div>
        
        {/* 3 Core Cards */}
        <div className="space-y-4 flex flex-col justify-between">
          <Card className="bg-white border-0 shadow-sm rounded-xl overflow-hidden group hover:shadow-md transition-all duration-300 flex-1 flex flex-col justify-center">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('customers.title')}</p>
                <h3 className="text-3xl font-bold text-[#1a1d2d]">{stats?.totalCustomers || 0}</h3>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center transition-transform group-hover:scale-110">
                <Users className="h-6 w-6 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm rounded-xl overflow-hidden group hover:shadow-md transition-all duration-300 flex-1 flex flex-col justify-center">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('dashboard.confirmed_shows')}</p>
                <h3 className="text-3xl font-bold text-[#1a1d2d]">{stats?.totalConfirmedShows || 0}</h3>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center transition-transform group-hover:scale-110">
                <Calendar className="h-6 w-6 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm rounded-xl overflow-hidden group hover:shadow-md transition-all duration-300 flex-1 flex flex-col justify-center">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('dashboard.scheduled_income')}</p>
                <div className="space-y-1">
                  {stats?.receivableByCurrency && Object.entries(stats.receivableByCurrency).map(([currency, total]) => (
                    <h3 key={currency} className="text-2xl xl:text-3xl font-bold text-[#1a1d2d]">
                      {(total as number).toLocaleString(i18n.language, { style: 'currency', currency, maximumFractionDigits: 0 })}
                    </h3>
                  ))}
                  {(!stats?.receivableByCurrency || Object.keys(stats.receivableByCurrency).length === 0) && (
                    <h3 className="text-2xl xl:text-3xl font-bold text-[#1a1d2d]">$0</h3>
                  )}
                </div>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center transition-transform group-hover:scale-110">
                <DollarSign className="h-6 w-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* BOTTOM SECTION: Top Clients & Recent Emails */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top 5 Clients */}
        <Card className="h-full">
          <CardHeader className="pb-3 border-b border-gray-50/50">
            <CardTitle className="text-md flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Top 5 Clientes
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {topClients.length > 0 ? (
              <div className="space-y-4">
                {topClients.map((client, index) => (
                  <div key={index} className="flex justify-between items-center group">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs group-hover:bg-primary group-hover:text-white transition-colors">
                        #{index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-semibold max-w-[200px] truncate">{client.name}</p>
                        <p className="text-xs text-muted-foreground">{client.eventCount} eventos</p>
                      </div>
                    </div>
                    <div className="font-bold text-sm text-[#1a1d2d]">
                      {client.totalRevenue.toLocaleString(i18n.language, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-8 text-center">No hay datos suficientes.</div>
            )}
          </CardContent>
        </Card>

        {/* Pipeline Summary Report */}
        <Card className="h-full flex flex-col">
          <CardHeader className="pb-3 border-b border-gray-50/50 flex flex-row items-center justify-between">
            <CardTitle className="text-md flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              {t('events.pipeline', 'Resumen de Pipeline')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 flex-1 flex flex-col gap-4">
            {['prospect', 'confirmed', 'show_completed', 'paid'].map(status => {
              const statusEvents = events.filter(e => e.status === status);
              const totalAmount = statusEvents.reduce((sum, e) => sum + (e.totalWithTax || e.cost || 0), 0);
              
              const statusConfig: Record<string, { label: string, color: string }> = {
                prospect: { label: 'Cotización', color: 'bg-amber-500' },
                confirmed: { label: 'Contrato', color: 'bg-blue-500' },
                show_completed: { label: 'Realizado', color: 'bg-purple-500' },
                paid: { label: 'Cobrado', color: 'bg-emerald-500' }
              };
              
              const config = statusConfig[status];

              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${config.color}`}></div>
                    <span className="text-sm font-medium">{config.label}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">{statusEvents.length} eventos</span>
                    <span className="font-bold w-24 text-right">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalAmount)}
                    </span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* ── EVENT VIEW MODAL (read-only) ─────────────────────────── */}
      <Dialog open={isViewModalOpen} onOpenChange={handleCloseView}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden rounded-2xl">
          {selectedEvent && (() => {
            const customer = customers.find(c => c.id === selectedEvent.customerId);
            const statusConfig: Record<string, { label: string; color: string }> = {
              prospect:       { label: 'Prospecto',        color: 'bg-amber-100 text-amber-700 border-amber-200' },
              confirmed:      { label: 'Confirmado',       color: 'bg-blue-100 text-blue-700 border-blue-200' },
              show_completed: { label: 'Show Completado',  color: 'bg-purple-100 text-purple-700 border-purple-200' },
              paid:           { label: 'Pagado',           color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
              cancelled:      { label: 'Cancelado',        color: 'bg-red-100 text-red-700 border-red-200' },
            };
            const st = statusConfig[selectedEvent.status] || { label: selectedEvent.status, color: 'bg-gray-100 text-gray-700' };
            const dateFormatted = selectedEvent.date
              ? format(new Date(selectedEvent.date), "EEEE, d 'de' MMMM yyyy", { locale: es })
              : 'Sin fecha';
            const amount = Number(selectedEvent.totalWithTax || selectedEvent.cost || 0)
              .toLocaleString(i18n.language, { style: 'currency', currency: (selectedEvent.currency as string) || 'USD' });
            return (
              <>
                {/* Header strip */}
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-5 border-b">
                  <DialogHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <Badge className={`${st.color} border text-xs font-semibold mb-2`}>{st.label}</Badge>
                        <DialogTitle className="text-xl font-bold leading-tight truncate">
                          {selectedEvent.title || 'Evento sin título'}
                        </DialogTitle>
                      </div>
                    </div>
                  </DialogHeader>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-4">

                  {/* Date & Time */}
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Calendar className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Fecha</p>
                      <p className="font-semibold text-sm capitalize">{dateFormatted}</p>
                      {(selectedEvent.startTime || selectedEvent.endTime) && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          <Clock className="h-3 w-3 inline mr-1" />
                          {selectedEvent.startTime || '--:--'} – {selectedEvent.endTime || '--:--'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Venue */}
                  {selectedEvent.venue && (
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <MapPin className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Lugar</p>
                        <p className="font-semibold text-sm">{selectedEvent.venue}</p>
                      </div>
                    </div>
                  )}

                  {/* Customer */}
                  {customer && (
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Users className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Cliente</p>
                        <p className="font-semibold text-sm">{customer.name}</p>
                        {customer.phone && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            <Phone className="h-3 w-3 inline mr-1" />{customer.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Amount */}
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <DollarSign className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Total a Cobrar</p>
                      <p className="font-bold text-lg text-emerald-700">{amount}</p>
                    </div>
                  </div>

                  {/* Packages / Items */}
                  {selectedEvent.items && selectedEvent.items.length > 0 && (
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Tag className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1.5">Paquetes / Servicios</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedEvent.items.map((item, i) => (
                            <span key={i} className="bg-muted text-muted-foreground text-xs px-2.5 py-1 rounded-full border">
                              {item.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Comments */}
                  {selectedEvent.comments && (
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Notas</p>
                        <p className="text-sm text-foreground/80 leading-relaxed">{selectedEvent.comments}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer actions */}
                <div className="px-6 py-4 bg-muted/30 border-t flex justify-between items-center">
                  <Button variant="ghost" size="sm" onClick={handleCloseView}>
                    Cerrar
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { handleCloseView(); navigate(`/events/${selectedEvent.id}`); }}
                    >
                      Ver detalle completo
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                    <Button size="sm" onClick={handleOpenEdit}>
                      <Edit className="h-4 w-4 mr-2" />
                      Editar
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Dashboard;
